import { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { CheckIcon } from '../../../../components/Icon';
import { i18n } from '../../../../language';
import { classNames, onKeyUpEnterOrSpace, escapeHtmlKeepUrls } from '../../../../utils';
import ehandoverIds from '../../../../ids/Ehandover';
import useCheckLogin from '../../../../utils/useCheckLogin';
import CompleteTaskPopup from '../Comments/CompleteTaskPopup';
import {
  getCompleteTaskApiParams,
  getTaskCompleteRemark,
  getUnCompleteTaskApiParams,
} from '../../../../utils/eHandoverUtil';

const useStyles = makeStyles((theme) => ({
  root: {},
  contextbody: {
    display: 'flex',
    marginBottom: '1.25rem',
    width: '100%',
  },
  contextlabel: {
    width: '100%',
    paddingLeft: '1.813rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: theme.palette.font.main,
    fontWeight: '500',
    backgroundColor: '#f5faff',
    borderRadius: '0.5rem',
    padding: '1rem 0',
    wordBreak: 'break-all',
  },
  contextfoot: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    margin: '-0.625rem 0 1.25rem 0',
  },
  complete: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.status.green,
    fontWeight: '500',
    textAlign: 'right',
    marginLeft: '0.625rem',
  },
  checkIcon: {
    fontSize: '0.75rem',
    color: theme.palette.status.green,
  },
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
    marginBottom: '0.625rem',
  },
  box: {
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '5px',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  checkbox: {
    backgroundColor: '#fff',
    border: 'solid 1px #c0c6cb',
  },
  checkedbox: {
    backgroundColor: '#28d2ab',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkBoxIcon: {
    color: theme.palette.font.white,
    fontSize: '0.813rem',
  },
  checkBoxBody: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '0.875rem',
  },
}));

const Checkbox = ({ id, check, handleChange }) => {
  const classes = useStyles();
  return (
    <div
      id={id}
      className={check ? classNames(classes.box, classes.checkedbox) : classNames(classes.box, classes.checkbox)}
      onClick={handleChange}
      tabIndex={0}
      onKeyUp={(e) => onKeyUpEnterOrSpace(e, handleChange)}
    >
      {check && <CheckIcon className={classes.checkBoxIcon} />}
    </div>
  );
};

export default function CheckBox({
  id,
  item,
  updateAPI,
  type,
  taskRemarksList = [],
  todayFlag,
  accessControl,
  setPendingAction,
  staffList,
  completeName,
  setCompleteName,
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const checkLogin = useCheckLogin();
  const loginInfo = useSelector((state) => state.loginInfo);
  const taskCompleteRemark = useMemo(() => getTaskCompleteRemark(taskRemarksList, item.ID), [taskRemarksList, item.ID]);
  const needLogin = accessControl?.isLoginUpdateItemYn === 'Y';

  const handleComplete = async () => {
    const loginStaffName = (loginInfo?.displayName || loginInfo?.corpID) ?? '';
    // api返回的corpId有可能跟当前登录的corpId有大小写区别，所以需要找回api的里面的corpId
    const finishNameFromApi = _.find(staffList, (item) => item?.value === completeName)?.text;
    const completeByName = needLogin ? loginStaffName : finishNameFromApi;
    const params = getCompleteTaskApiParams([item], completeByName);
    await updateAPI(params);
    setOpen(false);
    setCompleteName(loginInfo?.flag ? loginInfo.corpID.toUpperCase() : '');
  };

  const unCompleteTask = async () => {
    const params = getUnCompleteTaskApiParams([item], taskRemarksList);
    await updateAPI(params);
  };

  const completeTask = () => {
    // complete task 分两种情况
    // 如果setting页面没有勾需要登录 -> 保持以前一样，弹出可以选择用户
    // 如果勾上了 completeName 设置为当前登录用户
    if (!needLogin) {
      setOpen(true);
    } else {
      handleComplete();
    }
  };

  const handleOnChange = () => {
    if (['deleted', 'myDraft'].includes(type)) return;

    // 如果需要登录且未登录，标记待执行操作并弹出登录框
    if (needLogin && !checkLogin()) {
      setPendingAction({
        callback: (completeName) => {
          let params = null;
          if (item.IS_COMPLETE === 'Y') {
            params = getUnCompleteTaskApiParams([item], taskRemarksList);
          } else {
            params = getCompleteTaskApiParams([item], completeName);
          }
          updateAPI(params);
        },
      });
      return;
    }

    if (item.IS_COMPLETE === 'Y') {
      // uncomplete task
      unCompleteTask();
    } else {
      // complete task
      completeTask();
    }
  };

  return (
    <div>
      <div className={classes.contextbody}>
        <div className={classes.checkBoxBody}>
          <Checkbox
            id={ehandoverIds.checkNote + id + 1}
            check={item.IS_COMPLETE === 'Y'}
            handleChange={handleOnChange}
          />
        </div>
        <div
          className={classes.contextlabel}
          dangerouslySetInnerHTML={{
            __html: escapeHtmlKeepUrls(String(item.TITLE)),
          }}
          style={{ backgroundColor: todayFlag && '#ffffff' }}
        />
      </div>
      {item.IS_COMPLETE === 'Y' && taskCompleteRemark && (
        <div className={classes.contextfoot}>
          <CheckIcon className={classes.checkIcon} />
          <div className={classes.complete}>
            {i18n.t('EHANDOVER_TASKITEM_COMPLETED', {
              name: taskCompleteRemark.finishBy,
              time: moment(taskCompleteRemark.finishDate).format('YYYY-MM-DD HH:mm:ss'),
            })}
          </div>
        </div>
      )}
      <CompleteTaskPopup
        open={open}
        setOpen={setOpen}
        handleConfirm={handleComplete}
        id={id}
        name={completeName}
        setName={setCompleteName}
        staffList={staffList}
      />
    </div>
  );
}

export { Checkbox };
